import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowRight,
  FaBookOpen,
  FaChartLine,
  FaClock,
  FaStar,
} from "react-icons/fa";
import { useTheme } from "src/context/theme.context";
import { useGetLearnerAISessionsQuery } from "src/features/ai-sessions/ai-sessions.service";
import {
  AiSession,
  Score,
} from "src/features/ai-sessions/types/ai-session.entity";

type Props = {
  userId: string;
};

type PeriodMonths = 1 | 3 | 6 | 12;

const PERIODS: PeriodMonths[] = [1, 3, 6, 12];
const SCORE_KEYS: (keyof Omit<Score, "overall">)[] = [
  "grammar",
  "vocabulary",
  "fluency",
  "engagement",
  "relevancy",
];

const formatMinutes = (minutes: number) => {
  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (!hours) return `${remainingMinutes}m`;
  if (!remainingMinutes) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

const getAverage = (values: number[]) => {
  if (!values.length) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
};

const getPeriodStart = (months: PeriodMonths) => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
};

const getSessionDate = (session: AiSession) => {
  const startTime = session.startTime as unknown as { toDate?: () => Date };
  return startTime?.toDate
    ? startTime.toDate()
    : new Date(session.startTime as unknown as Date);
};

const getMonthKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}`;

const getSkillLabel = (skill: keyof Omit<Score, "overall">) =>
  skill.charAt(0).toUpperCase() + skill.slice(1);

export const UserAiPractice: React.FC<Props> = ({ userId }) => {
  const { isDarkMode } = useTheme();
  const [periodMonths, setPeriodMonths] = useState<PeriodMonths>(12);
  const dateRange = useMemo(
    () => ({
      startDate: getPeriodStart(periodMonths),
      endDate: new Date(),
    }),
    [periodMonths],
  );
  const {
    data: sessions = [],
    isLoading,
    isFetching,
    isError,
  } = useGetLearnerAISessionsQuery({
    userId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const statistics = useMemo(() => {
    const completedSessions = sessions.filter(
      (session) => session.status === "COMPLETED",
    );
    const totalPracticeMinutes = completedSessions.reduce(
      (total, session) => total + (session.durationInMins ?? 0),
      0,
    );
    const scoredSessions = completedSessions.filter(
      (session) => typeof session.score?.overall === "number",
    );

    return {
      averageDuration: completedSessions.length
        ? totalPracticeMinutes / completedSessions.length
        : 0,
      averageScore: getAverage(
        scoredSessions.map((session) => session.score!.overall),
      ),
      completedSessions,
      scoredSessionsCount: scoredSessions.length,
      skills: SCORE_KEYS.map((skill) => {
        const relevantSessions = scoredSessions.filter(
          (session) =>
            session.context?.sessionMode !== "WORD_BANK" ||
            skill === "vocabulary",
        );
        return {
          label: getSkillLabel(skill),
          value: getAverage(
            relevantSessions
              .map((session) => session.score?.[skill])
              .filter((score): score is number => typeof score === "number"),
          ),
        };
      }),
      totalPracticeMinutes,
    };
  }, [sessions]);

  const activityData = useMemo(() => {
    const months = Array.from({ length: periodMonths }, (_, index) => {
      const month = new Date(
        dateRange.startDate.getFullYear(),
        dateRange.startDate.getMonth() + index,
        1,
      );
      return {
        key: getMonthKey(month),
        minutes: 0,
      };
    });
    const monthsByKey = new Map(months.map((month) => [month.key, month]));

    statistics.completedSessions.forEach((session) => {
      const month = monthsByKey.get(getMonthKey(getSessionDate(session)));
      if (month) month.minutes += session.durationInMins ?? 0;
    });

    return months;
  }, [dateRange.startDate, periodMonths, statistics.completedSessions]);

  const cardClass = isDarkMode
    ? "bg-gradient-to-br from-gray-900/80 to-gray-800/80 border-gray-700"
    : "bg-gradient-to-br from-white to-violet-50 border-violet-100";
  const textClass = isDarkMode ? "text-white" : "text-gray-900";
  const mutedClass = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <div className={`rounded-3xl border shadow-2xl p-8 ${cardClass}`}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600">
            <FaChartLine className="text-lg text-white" />
          </div>
          <div>
            <h3 className={`text-2xl font-semibold ${textClass}`}>
              AI Practice
            </h3>
            <p className={`text-sm ${mutedClass}`}>
              Completed AI coaching sessions and performance.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((months) => (
            <button
              key={months}
              type="button"
              onClick={() => setPeriodMonths(months)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                periodMonths === months
                  ? "bg-violet-600 text-white"
                  : isDarkMode
                    ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    : "bg-white text-gray-600 hover:bg-violet-100"
              }`}
            >
              Last {months} {months === 1 ? "month" : "months"}
            </button>
          ))}
          <Link
            to={`/ai-sessions?userId=${userId}`}
            className="ml-1 inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1.5 text-xs font-semibold text-violet-700 no-underline transition-colors hover:bg-violet-200"
          >
            View sessions
            <FaArrowRight />
          </Link>
        </div>
      </div>

      {isLoading || isFetching ? (
        <div className={`py-16 text-center ${mutedClass}`}>
          Loading AI practice…
        </div>
      ) : isError ? (
        <div className="py-16 text-center text-red-500">
          Unable to load AI practice statistics.
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatisticCard
              icon={<FaClock />}
              label="Practice time"
              value={formatMinutes(statistics.totalPracticeMinutes)}
              isDarkMode={isDarkMode}
            />
            <StatisticCard
              icon={<FaBookOpen />}
              label="Completed sessions"
              value={statistics.completedSessions.length.toString()}
              isDarkMode={isDarkMode}
            />
            <StatisticCard
              icon={<FaClock />}
              label="Average duration"
              value={formatMinutes(statistics.averageDuration)}
              isDarkMode={isDarkMode}
            />
            <StatisticCard
              icon={<FaStar />}
              label="Average score"
              value={
                statistics.averageScore === null
                  ? "—"
                  : `${statistics.averageScore.toFixed(1)} / 10`
              }
              isDarkMode={isDarkMode}
            />
          </div>

          <div
            className={`rounded-2xl border p-6 ${
              isDarkMode
                ? "border-gray-700 bg-gray-900/40"
                : "border-violet-100 bg-white/70"
            }`}
          >
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h4 className={`font-semibold ${textClass}`}>
                  Practice activity
                </h4>
                <p className={`text-sm ${mutedClass}`}>
                  Minutes practiced over the selected period.
                </p>
              </div>
              <span className={`text-sm font-medium ${mutedClass}`}>
                {statistics.completedSessions.length} completed
              </span>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={activityData}
                  margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke={isDarkMode ? "#374151" : "#ede9fe"}
                  />
                  <XAxis dataKey="key" hide />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: isDarkMode ? "#9ca3af" : "#6b7280",
                      fontSize: 12,
                    }}
                  />
                  <Tooltip
                    cursor={{ fill: isDarkMode ? "#312e81" : "#f5f3ff" }}
                    formatter={(value: number) => [`${value} min`, "Practice"]}
                    labelFormatter={() => "Practice activity"}
                    contentStyle={{
                      borderRadius: 12,
                      border: "none",
                      backgroundColor: isDarkMode ? "#111827" : "#ffffff",
                      color: isDarkMode ? "#ffffff" : "#111827",
                    }}
                  />
                  <Bar
                    dataKey="minutes"
                    fill="#7c3aed"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={44}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {statistics.skills.map((skill) => (
              <div
                key={skill.label}
                className={`rounded-2xl border p-4 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-900/40"
                    : "border-violet-100 bg-white/70"
                }`}
              >
                <div className={`text-sm ${mutedClass}`}>{skill.label}</div>
                <div className={`mt-2 text-2xl font-bold ${textClass}`}>
                  {skill.value === null
                    ? "—"
                    : `${skill.value.toFixed(1)} / 10`}
                </div>
              </div>
            ))}
          </div>

          {statistics.completedSessions.length === 0 && (
            <div
              className={`rounded-2xl border border-dashed p-6 text-center ${mutedClass}`}
            >
              No completed AI practice sessions in this period.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type StatisticCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  isDarkMode: boolean;
};

const StatisticCard: React.FC<StatisticCardProps> = ({
  icon,
  label,
  value,
  isDarkMode,
}) => (
  <div
    className={`rounded-2xl border p-5 ${
      isDarkMode
        ? "border-gray-700 bg-gray-900/40"
        : "border-violet-100 bg-white/70"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="text-violet-500">{icon}</span>
      <span
        className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </span>
    </div>
    <div
      className={`mt-4 text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
    >
      {value}
    </div>
  </div>
);
